import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

/** Show “delivered within 24 hours” copy only for this long after Full Report payment */
export const REPORT_DELIVERY_WINDOW_MS = 24 * 60 * 60 * 1000;

/** True if `reportPurchaseAt` is within the first 24 hours after payment (server ISO time). */
export function isWithinReportDeliveryWindow(reportPurchaseAt) {
  if (!reportPurchaseAt) return false;
  const elapsed = Date.now() - new Date(reportPurchaseAt).getTime();
  return elapsed >= 0 && elapsed < REPORT_DELIVERY_WINDOW_MS;
}

/**
 * usePayment — fetches & caches purchase status per user+member
 *
 * Returns:
 *   accessLevel   null | 'dashboard' | 'report'
 *   isDashboard   boolean   (dashboard or report purchased)
 *   isReport      boolean   (report purchased)
 *   reportPurchaseAt  string | null  ISO time of latest Full Report payment
 *   pdfAddonPurchased  boolean  (₹99 PDF add-on for dashboard members)
 *   loading       boolean
 *   refresh       () => void
 */
export function usePayment(userId, familyMemberId) {
  const [accessLevel, setAccessLevel] = useState(null);
  const [reportPurchaseAt, setReportPurchaseAt] = useState(null);
  const [pdfAddonPurchased, setPdfAddonPurchased] = useState(false);
  const [loading, setLoading]         = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!userId || !familyMemberId) {
      setAccessLevel(null);
      setReportPurchaseAt(null);
      setPdfAddonPurchased(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/payments/status?userId=${encodeURIComponent(userId)}&familyMemberId=${encodeURIComponent(familyMemberId)}`
      );
      const data = await res.json();
      if (data.success) {
        setAccessLevel(data.accessLevel);
        setReportPurchaseAt(data.reportPurchaseAt || null);
        setPdfAddonPurchased(!!data.pdfAddonPurchased);
      }
    } catch (err) {
      console.error('usePayment: fetch error', err);
    } finally {
      setLoading(false);
    }
  }, [userId, familyMemberId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  return {
    accessLevel,
    isDashboard: accessLevel === 'dashboard' || accessLevel === 'report',
    isReport:    accessLevel === 'report',
    reportPurchaseAt,
    pdfAddonPurchased,
    loading,
    refresh: fetchStatus,
  };
}
